import React from 'react';

const PrivacyPolicyPage = () => {
  return (
    <section className="pt-24 pb-16 sm:pt-32 sm:pb-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-bold mb-8">Privacy Policy</h1>
          <div className="prose prose-lg">

            <p className="mb-4">
              Your privacy is important to us. It is Vermillion AI's policy to respect your privacy and comply with any applicable law and regulation
              regarding any personal information we may collect about you, including across our website, <a
              href="https://vermillion-ai.com">https://vermillion-ai.com</a>, and other sites we
              own and operate.
            </p>

            <p className="mb-4">
              Personal information is any information about you which can be used to identify you. This includes information about you as a person
              (such as name, address, and date of birth), your devices, payment details, and even information about how you use a website or online
              service.
            </p>

            <p className="mb-4">
              In the event our site contains links to third-party sites and services, please be aware that those sites and services have their own
              privacy policies. After following a link to any third-party content, you should read their posted privacy policy information about how
              they collect and use personal information. This Privacy Policy does not apply to any of your activities after you leave our site.
            </p>

            <p className="mb-4">
              This policy is effective as of May 16, 2025
            </p>

            <p className=" mb-6">
              Last updated: May 16, 2025
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Information We Collect</h2>
            <p className="mb-4">
              Information we collect falls into one of two categories: "voluntarily provided" information and "automatically collected" information.
            </p>

            <p className="mb-4">
              "Voluntarily provided" information refers to any information you knowingly and actively provide us when using or participating in any of
              our services and promotions.
            </p>

            <p className="mb-4">
              "Automatically collected" information refers to any information automatically sent by your devices in the course of accessing our
              products and services.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Log Data</h2>
            <p className="mb-4">
              When you visit our website, our servers may automatically log the standard data provided by your web browser. It may include your
              device's Internet Protocol (IP) address, your browser type and version, the pages you visit, the time and date of your visit, the time
              spent on each page, and other details about your visit.
            </p>

            <p className="mb-4">
              Additionally, if you encounter certain errors while using the site, we may automatically collect data about the error and the
              circumstances surrounding its occurrence. This data may include technical details about your device, what you were trying to do when the
              error happened, and other technical information relating to the problem. You may or may not receive notice of such errors, even in the
              moment they occur, that they have occurred, or what the nature of the error is.
            </p>

            <p className="mb-4">
              Please be aware that while this information may not be personally identifying by itself, it may be possible to combine it with other
              data to personally identify individual persons.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Security of Your Personal Information</h2>
            <p className="mb-4">
              When we collect and process personal information, and while we retain this information, we will protect it within commercially
              acceptable means to prevent loss and theft, as well as unauthorized access, disclosure, copying, use or modification.
            </p>

            <p className="mb-4">
              Although we will do our best to protect the personal information you provide to us, we advise that no method of electronic transmission
              or storage is 100% secure and no one can guarantee absolute data security.
            </p>

            <p className="mb-4">
              You are responsible for selecting any password and its overall security strength, ensuring the security of your own information within
              the bounds of our services. For example, ensuring you do not make your personal information publicly available via our platform.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">How Long We Keep Your Personal Information</h2>
            <p className="mb-4">
              We keep your personal information only for as long as we need to. This time period may depend on what we are using your information for,
              in accordance with this privacy policy. For example, if you have provided us with personal information such as an email address when
              contacting us about a specific enquiry, we may retain this information for the duration of your enquiry remaining open as well as for
              our own records so we may effectively address similar enquiries in future. If your personal information is no longer required for this
              purpose, we will delete it or make it anonymous by removing all details that identify you.
            </p>

            <p className="mb-4">
              However, if necessary, we may retain your personal information for our compliance with a legal, accounting, or reporting obligation or
              for archiving purposes in the public interest, scientific, or historical research purposes or statistical purposes.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Children's Privacy</h2>
            <p className="mb-4">
              We do not aim any of our products or services directly at children under the age of 13 and we do not knowingly collect personal
              information about children under 13.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Disclosure of Personal Information to Third Parties</h2>
            <p className="mb-4">
              We may disclose personal information to:
            </p>
            <ul className="list-disc pl-6 mb-4 ">
              <li>a parent, subsidiary or affiliate of our company</li>
              <li>third-party service providers for the purpose of enabling them to provide their services, including (without limitation) IT service
                providers, data storage, hosting and server providers, analytics, error loggers, debt collectors, maintenance or problem-solving
                providers, professional advisors, and payment systems operators
              </li>
              <li>our employees, contractors, and/or related entities</li>
              <li>our existing or potential agents or business partners</li>
              <li>credit reporting agencies, courts, tribunals, and regulatory authorities, in the event you fail to pay for goods or services we have
                provided to you
              </li>
              <li>courts, tribunals, regulatory authorities, and law enforcement officers, as required by law, in connection with any actual or
                prospective legal proceedings, or in order to establish, exercise, or defend our legal rights
              </li>
              <li>third parties, including agents or sub-contractors who assist us in providing information, products, services, or direct marketing
                to you
              </li>
              <li>third parties to collect and process data</li>
              <li>an entity that buys, or to which we transfer all or substantially all of our assets and business</li>
            </ul>

            <p className="mb-4">
              Third parties we currently use include:
            </p>
            <ul className="list-disc pl-6 mb-4 ">
              <li>Google Analytics</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Your Rights and Controlling Your Personal Information</h2>
            <p className="mb-4">
              <strong>Your choice:</strong> By providing personal information to us, you understand we will collect, hold, use, and disclose your
              personal information in accordance with this privacy policy. You do not have to provide personal information to us, however, if you do
              not, it may affect your use of our website or the products and/or services offered on or through it.
            </p>

            <p className="mb-4">
              <strong>Information from third parties:</strong> If we receive personal information about you from a third party, we will protect it as
              set out in this privacy policy. If you are a third party providing personal information about somebody else, you represent and warrant
              that you have such person's consent to provide the personal information to us.
            </p>

            <p className="mb-4">
              <strong>Marketing permission:</strong> If you have previously agreed to us using your personal information for direct marketing
              purposes, you may change your mind at any time by contacting us using the details below.
            </p>

            <p className="mb-4">
              <strong>Access:</strong> You may request details of the personal information that we hold about you.
            </p>

            <p className="mb-4">
              <strong>Correction:</strong> If you believe that any information we hold about you is inaccurate, out of date, incomplete, irrelevant,
              or misleading, please contact us using the details provided in this privacy policy. We will take reasonable steps to correct any
              information found to be inaccurate, incomplete, misleading, or out of date.
            </p>

            <p className="mb-4">
              <strong>Non-discrimination:</strong> We will not discriminate against you for exercising any of your rights over your personal
              information. Unless your personal information is required to provide you with a particular service or offer (for example providing user
              support), we will not deny you goods or services and/or charge you different prices or rates for goods or services, including through
              granting discounts or other benefits, or imposing penalties, or provide you with a different level or quality of goods or services.
            </p>

            <p className="mb-4">
              <strong>Notification of data breaches:</strong> We will comply with laws applicable to us in respect of any data breach.
            </p>

            <p className="mb-4">
              <strong>Complaints:</strong> If you believe that we have breached a relevant data protection law and wish to make a complaint, please
              contact us using the details below and provide us with full details of the alleged breach. We will promptly investigate your complaint
              and respond to you, in writing, setting out the outcome of our investigation and the steps we will take to deal with your complaint. You
              also have the right to contact a regulatory body or data protection authority in relation to your complaint.
            </p>

            <p className="mb-4">
              <strong>Unsubscribe:</strong> To unsubscribe from our email database or opt-out of communications (including marketing communications),
              please contact us using the details provided in this privacy policy, or opt-out using the opt-out facilities provided in the
              communication. We may need to request specific information from you to help us confirm your identity.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Use of Cookies</h2>
            <p className="mb-4">
              We use "cookies" to collect information about you and your activity across our site. A cookie is a small piece of data that our website
              stores on your computer, and accesses each time you visit, so we can understand how you use our site. This helps us serve you content
              based on preferences you have specified.
            </p>

            <p className="mb-4">
              Please refer to our Cookie Policy for more information.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Business Transfers</h2>
            <p className="mb-4">
              If we or our assets are acquired, or in the unlikely event that we go out of business or enter bankruptcy, we would include data,
              including your personal information, among the assets transferred to any parties who acquire us. You acknowledge that such transfers may
              occur, and that any parties who acquire us may, to the extent permitted by applicable law, continue to use your personal information
              according to this policy, which they will be required to assume as it is the basis for any ownership or use rights we have over such
              information.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Limits of Our Policy</h2>
            <p className="mb-4">
              Our website may link to external sites that are not operated by us. Please be aware that we have no control over the content and
              policies of those sites, and cannot accept responsibility or liability for their respective privacy practices.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Changes to This Policy</h2>
            <p className="mb-4">
              At our discretion, we may change our privacy policy to reflect updates to our business processes, current acceptable practices, or
              legislative or regulatory changes. If we decide to change this privacy policy, we will post the changes here at the same link by which
              you are accessing this privacy policy.
            </p>

            <p className="mb-4">
              If required by law, we will get your permission or give you the opportunity to opt in to or opt out of, as applicable, any new uses of
              your personal information.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Additional Disclosures for U.S. States Privacy Law Compliance</h2>
            <p className="mb-4">
              The following section includes provisions that comply with the privacy laws of these states (California, Colorado, Delaware, Florida,
              Virginia, and Utah) and is applicable only to the residents of those states. Specific references to a particular state (in a heading or
              in the text) are only a reference to that state's law and applies only to that state's residents. Non-state specific language applies to
              all of the states listed above.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Do Not Track</h3>
            <p className="mb-4">
              Some browsers have a "Do Not Track" feature that lets you tell websites that you do not want to have your online activities tracked. At
              this time, we do not respond to browser "Do Not Track" signals.
            </p>

            <p className="mb-4">
              We adhere to the standards outlined in this privacy policy, ensuring we collect and process personal information lawfully, fairly,
              transparently, and with legitimate, legal reasons for doing so.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Cookies and Pixels</h3>
            <p className="mb-4">
              At all times, you may decline cookies from our site if your browser permits. Most browsers allow you to activate settings on your
              browser to refuse the setting of all or some cookies. Accordingly, your ability to limit cookies is based only on your browser's
              capabilities. Please refer to the Cookies section of this privacy policy for more information.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">California Privacy Laws - CPPA</h3>
            <p className="mb-4">
              Under California Civil Code Section 1798.83, if you live in California and your business relationship with us is mainly for personal,
              family, or household purposes, you may ask us about the information we release to other organizations for their marketing purposes. In
              accordance with your right to non-discrimination, we may offer you certain financial incentives permitted by the California Consumer
              Privacy Act, and the California Privacy Rights Act (collectively, CCPA) that can result in different prices, rates, or quality levels
              for the goods or services we provide. Any CCPA-permitted financial incentive we offer will reasonably relate to the value of your
              personal information, and we will provide written terms that describe clearly the nature of such an offer. Participation in a financial
              incentive program requires your prior opt-in consent, which you may revoke at any time.
            </p>

            <p className="mb-4">
              Under California Civil Code Section 1798.83, if you live in California and your business relationship with us is mainly for personal,
              family, or household purposes, you may ask us about the information we release to other organizations for their marketing purposes. To
              make such a request, please contact us using the details provided in this privacy policy with "Request for California privacy
              information" in the subject line. You may make this type of request once every calendar year. We will email you a list of categories of
              personal information we revealed to other organisations for their marketing purposes in the last calendar year, along with their names
              and addresses. Not all personal information shared in this way is covered by Section 1798.83 of the California Civil Code.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">California Notice of Collection</h3>
            <p className="mb-4">
              In the past 12 months, we have collected the following categories of personal information enumerated in the CCPA:
            </p>

            <p className="mb-4">
              For more information on information we collect, including the sources we receive information from, review the "Information We Collect"
              section. We collect and use these categories of personal information for the business purposes described in the "Collection and Use of
              Information" section, including to provide and manage our Service.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Right to Know and Delete</h3>
            <p className="mb-4">
              You have rights to delete your personal information we collected and know certain information about our data practices in the preceding
              12 months. In particular, you have the right to request the following from us:
            </p>
            <ul className="list-disc pl-6 mb-4 ">
              <li>The categories of personal information we have collected about you;</li>
              <li>The categories of sources from which the personal information was collected;</li>
              <li>The categories of personal information about you we disclosed for a business purpose or sold;</li>
              <li>The categories of third parties to whom the personal information was disclosed for a business purpose or sold;</li>
              <li>The business or commercial purpose for collecting or selling the personal information; and</li>
              <li>The specific pieces of personal information we have collected about you.</li>
            </ul>

            <p className="mb-4">
              To exercise any of these rights, please contact us using the details provided in this privacy policy.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Shine the Light</h3>
            <p className="mb-4">
              In addition to the rights discussed above, you have the right to request information from us regarding the manner in which we share
              certain personal information as defined by applicable statute with third parties and affiliates for their own direct marketing purposes.
            </p>

            <p className="mb-4">
              To receive this information, send us a request using the contact details provided in this privacy policy. Requests must include "Privacy
              Rights Request" in the first line of the description and include your name, street address, city, state, and ZIP code.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Additional Disclosures for General Data Protection Regulation (GDPR)
              Compliance (EU)</h2>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Data Controller / Data Processor</h3>
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Legal Bases for Processing Your Personal Information</h3>
            <p className="mb-4">
              We will only collect and use your personal information when we have a legal right to do so. In which case, we will collect and use your
              personal information lawfully, fairly, and in a transparent manner. If we seek your consent to process your personal information, and
              you are under 16 years of age, we will seek your parent or legal guardian's consent to process your personal information for that
              specific purpose.
            </p>

            <p className="mb-4">
              Our lawful bases depend on the services you use and how you use them. This means we only collect and use your information on the
              following grounds:
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Consent From You</h3>
            <p className="mb-4">
              Where you give us consent to collect and use your personal information for a specific purpose. You may withdraw your consent at any time
              using the facilities we provide; however this will not affect any use of your information that has already taken place. When you contact
              us, you may consent to your name and email address being used so we can respond to your enquiry. While you may request that we delete
              your contact details at any time, we cannot recall any email we have already sent. If you have any further enquiries about how to
              withdraw your consent, please feel free to enquire using the details provided in the Contact Us section of this privacy policy.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Performance of a Contract or Transaction</h3>
            <p className="mb-4">
              Where you have entered into a contract or transaction with us, or in order to take preparatory steps prior to our entering into a
              contract or transaction with you. For example, if you contact us with an enquiry, we may require personal information such as your name
              and contact details in order to respond.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Our Legitimate Interests</h3>
            <p className="mb-4">
              Where we assess it is necessary for our legitimate interests, such as for us to provide, operate, improve and communicate our services.
              We consider our legitimate interests to include research and development, understanding our audience, marketing and promoting our
              services, measures taken to operate our services efficiently, marketing analysis, and measures taken to protect our legal rights and
              interests.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Compliance with Law</h3>
            <p className="mb-4">
              In some cases, we may have a legal obligation to use or keep your personal information. Such cases may include (but are not limited to)
              court orders, criminal investigations, government requests, and regulatory obligations. If you have any further enquiries about how we
              retain personal information in order to comply with the law, please feel free to enquire using the details provided in the Contact Us
              section of this privacy policy.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">International Transfers Outside of the European Economic Area (EEA)</h3>
            <p className="mb-4">
              We will ensure that any transfer of personal information from countries in the European Economic Area (EEA) to countries outside the EEA
              will be protected by appropriate safeguards, for example by using standard data protection clauses approved by the European Commission,
              or the use of binding corporate rules or other legally accepted means.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Your Rights and Controlling Your Personal Information</h3>
            <p className="mb-4">
              <strong>Restrict:</strong> You have the right to request that we restrict the processing of your personal information if:
            </p>
            <ul className="list-disc pl-6 mb-4 ">
              <li>you are concerned about the accuracy of your personal information;</li>
              <li>you believe your personal information has been unlawfully processed;</li>
              <li>you need us to maintain the personal information solely for the purpose of a legal claim; or</li>
              <li>we are in the process of considering your objection in relation to processing on the basis of legitimate interests.</li>
            </ul>

            <p className="mb-4">
              <strong>Objecting to processing:</strong> You have the right to object to processing of your personal information that is based on our
              legitimate interests or public interest. If this is done, we must provide compelling legitimate grounds for the processing which
              overrides your interests, rights, and freedoms, in order to proceed with the processing of your personal information.
            </p>

            <p className="mb-4">
              <strong>Data portability:</strong> You may have the right to request a copy of the personal information we hold about you. Where
              possible, we will provide this information in CSV format or other easily readable machine format. You may also have the right to request
              that we transfer this personal information to a third party.
            </p>

            <p className="mb-4">
              <strong>Deletion:</strong> You may have a right to request that we delete the personal information we hold about you at any time, and we
              will take reasonable steps to delete your personal information from our current records. If you ask us to delete your personal
              information, we will let you know how the deletion affects your use of our website or products and services. There may be exceptions to
              this right for specific legal reasons which, if applicable, we will set out for you in response to your request. If you terminate or
              delete your account, we will delete your personal information within 7 days of the deletion of your account. Please be aware that search
              engines and similar third parties may still retain copies of your personal information that has been made public at least once, like
              certain profile information and public comments, even after you have deleted the information from our services or deactivated your
              account.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Contact Us</h2>
            <p className="mb-4">
              For any questions or concerns regarding your privacy, you may contact us using the following details:
            </p>
            <p className="mb-4">
              <strong>Contact Us</strong><br/>
              contact@vermillion-ai.com
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicyPage;